/**
 * SSE (Server-Sent Events) Client
 * 
 * Centralized SSE client with:
 * - Automatic JWT token injection (same as axiosInstance)
 * - Base URL from env config
 * - Connection management (connect/disconnect)
 * - Error handling and logging
 * - TypeScript support
 * 
 * Note: Uses fetch API because native EventSource doesn't support custom headers.
 */

import { env } from '@/config/env';
import { getAuthToken } from '../axios';

/**
 * SSE Connection Options
 */
export interface SSEConnectionOptions {
  /** Custom API base URL (defaults to env.API_URL) */
  baseUrl?: string;
  /** Skip auth token (for public endpoints) */
  skipAuth?: boolean;
  /** Custom headers to include */
  headers?: Record<string, string>;
  /** Connection timeout in ms (default: 10000) */
  timeout?: number;
  /** Callback when connection opens */
  onOpen?: () => void;
  /** Callback when connection closes */
  onClose?: () => void;
  /** Callback when connection errors */
  onError?: (error: Error) => void;
}

/**
 * SSE Connection Result
 */
export interface SSEConnection {
  /** Abort the connection */
  abort: () => void;
  /** Check if connection is active */
  isConnected: () => boolean;
}

/**
 * Create SSE connection with automatic auth and base URL
 * 
 * @param endpoint - API endpoint (e.g., '/deployments/{id}/progress')
 * @param onMessage - Callback for each SSE message
 * @param options - Connection options
 * @returns SSE connection controls
 * 
 * @example
 * ```typescript
 * const connection = createSSEConnection(
 *   `/deployments/${deploymentId}/progress`,
 *   (data) => {
 *     console.log('Received:', data);
 *     if (data.status === 'succeeded') {
 *       connection.abort();
 *     }
 *   },
 *   {
 *     onOpen: () => console.log('Connected'),
 *     onError: (err) => console.error('Error:', err),
 *     onClose: () => console.log('Disconnected'),
 *   }
 * );
 * 
 * // Later: abort the connection
 * connection.abort();
 * ```
 */
export function createSSEConnection<T = any>(
  endpoint: string,
  onMessage: (data: T) => void,
  options: SSEConnectionOptions = {}
): SSEConnection {
  const { baseUrl: customBaseUrl, skipAuth = false, headers = {}, timeout = 10000, onOpen, onClose, onError } = options;

  const abortController = new AbortController();
  let connected = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  // Build full URL (use custom baseUrl or default from env)
  const baseUrl = customBaseUrl || env.API_URL;
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  // Start connection
  (async () => {
    try {
      // Get auth token (same as axiosInstance)
      const token = skipAuth ? null : getAuthToken();

      // Build headers
      const requestHeaders: Record<string, string> = {
        // 'Accept': 'text/event-stream',
        ...headers,
      };

      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }

      // Log in development
      if (env.NCODER_ENV === 'local') {
        console.log(`📡 SSE Connecting: ${url}`, {
          skipAuth,
          hasToken: !!token,
        });
      }

      // Set connection timeout
      timeoutId = setTimeout(() => {
        if (!connected) {
          abortController.abort();
          const timeoutError = new Error(`SSE connection timeout after ${timeout}ms. This may be due to CORS issues or server unavailability.`);
          console.error('📡 SSE Timeout:', timeoutError.message);
          onError?.(timeoutError);
          onClose?.();
        }
      }, timeout);

      const response = await fetch(url, {
        method: 'GET',
        headers: requestHeaders,
        signal: abortController.signal,
      });

      // Clear timeout since we got a response
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Handle HTTP errors
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`SSE HTTP Error ${response.status}: ${errorText}`);
      }

      // Mark as connected
      connected = true;
      onOpen?.();

      if (env.NCODER_ENV === 'local') {
        console.log(`📡 SSE Connected: ${url}`);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          if (env.NCODER_ENV === 'local') {
            console.log(`📡 SSE Stream ended: ${url}`);
          }
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // SSE format: "data: {...}"
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6); // Remove "data: " prefix
              const data = JSON.parse(jsonStr) as T;

              if (env.NCODER_ENV === 'local') {
                console.log(`📡 SSE Event:`, data);
              }

              onMessage(data);
            } catch (parseError) {
              console.error('📡 SSE Parse Error:', parseError, 'Line:', line);
            }
          }
          // Ignore other SSE fields (event:, id:, retry:, comments)
        }
      }

      // Stream ended normally
      connected = false;
      onClose?.();

    } catch (error: any) {
      connected = false;

      // Clear timeout if still pending
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Ignore abort errors (intentional disconnection)
      if (error.name === 'AbortError') {
        if (env.NCODER_ENV === 'local') {
          console.log(`📡 SSE Aborted: ${url}`);
        }
        onClose?.();
        return;
      }

      // Detect CORS/Network errors (fetch gives generic TypeError for CORS)
      let enhancedError = error;
      if (error.name === 'TypeError' && error.message?.includes('Failed to fetch')) {
        enhancedError = new Error(
          'Failed to connect to SSE stream. This is likely a CORS issue - the server may not be configured to allow requests from this origin.'
        );
        console.error('📡 SSE CORS/Network Error:', {
          url,
          origin: window.location.origin,
          message: 'Server needs to configure CORS headers for this endpoint',
        });
      } else {
        console.error(`📡 SSE Error: ${url}`, error);
      }

      onError?.(enhancedError);
      onClose?.();
    }
  })();

  // Return connection controls
  return {
    abort: () => {
      abortController.abort();
      connected = false;
    },
    isConnected: () => connected,
  };
}

/**
 * Helper to create typed SSE connection
 * 
 * @example
 * ```typescript
 * interface DeploymentEvent {
 *   status: 'queued' | 'in_progress' | 'succeeded' | 'failed';
 *   message: string;
 * }
 * 
 * const connection = createTypedSSEConnection<DeploymentEvent>(
 *   `/deployments/${id}/progress`,
 *   (event) => {
 *     // event is typed as DeploymentEvent
 *     console.log(event.status, event.message);
 *   }
 * );
 * ```
 */
export const createTypedSSEConnection = createSSEConnection;

export default createSSEConnection;
